//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { CollectionSortType } from '@apis/collections/CollectionSortType';
import { ICollectionFiltersDto } from '@apis/collections/ICollectionFiltersDto';
import { ICollectionResponse } from '@apis/collections/ICollectionResponse';
import { ICollectionSortDto } from '@apis/collections/ICollectionSortDto';
import { ICollectionSourcesDto } from '@apis/collections/ICollectionSourcesDto';
import {
  ContentLibrarySortType,
  ContentLibrarySourcesGroups,
  IContentLibraryFiltersOptions,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import {
  ISettingsUser,
  IUserInfo,
} from '@apis/team-management/teamManagementApiModel';
import { CONTENT_COLLECTION_CONTEXTS } from '@legacy-components/content/content-collections-models';
import { ICollectionSidebarState } from '@legacy-components/content/content-collections-sidebar';
import { MediaLibrarySubPages } from '@media-library/constants/mediaLibraryConstants';
import ClientId from '@models/ClientId';
import { isDefined } from '@utils/filterPredicates';

import { deserializeOptionalEnumParam } from 'app/initialization/routing/stateParamModels';
import { RoutableStateName } from 'app/top-level-states';

/**
 * Filter users by client id.
 * - If the client name is 'All Brands', return all users.
 * - Return all users that do not have `clientIds` property (basic users).
 * - Return admin users whose `clientIds` includes the current client's id.
 * @param clientId Client id
 * @param clientName Client name
 * @param users All users associated with the current client
 * @returns All users in current client
 */
export const clientFilter = (
  clientId: ClientId,
  clientName: string,
  users: ISettingsUser[] | IUserInfo[],
) => {
  return clientName === 'All Brands'
    ? users
    : users.filter(
        (user: ISettingsUser | IUserInfo) =>
          !('clientIds' in user) || user.clientIds.includes(clientId),
      );
};

/**
 * Map a list of collections from API response to media library sidebar collection states.
 * @param collections List of collections fetched from the API
 * @returns List of collection states for media library sidebar
 */
export const mapCollectionsToSidebarStates = (
  collections: ICollectionResponse[],
): ICollectionSidebarState[] => {
  return collections.map(({ id, name }) => ({
    name: name,
    state: {
      view: CONTENT_COLLECTION_CONTEXTS.COLLECTIONS,
      params: {
        collectionId: id,
      },
    },
  }));
};

/**
 * Map media library filters params to filters dto for create or update collections.
 * @param filters Media library filters params
 * @returns Collection filters dto
 */
export const mapMediaLibraryFiltersToCollectionFiltersDto = ({
  dateRange,
  distribution = [],
  hasLabels,
  hasProducts,
  igHashtags = [],
  igUsernames = [],
  isDistributed,
  labels = [],
  mediaItemTypes = [],
  orientation = [],
  rightsStatuses = [],
  semanticTags = [],
  spamFiltering,
}: IContentLibraryFiltersOptions): ICollectionFiltersDto => {
  return {
    dateRange: dateRange,
    distribution: isDefined(isDistributed)
      ? {
          options: distribution,
          isDistributed: isDistributed,
        }
      : undefined,
    instagramHashtags: { options: igHashtags },
    instagramUsers: { options: igUsernames },
    labels: { options: labels, hasLabels },
    mediaTypes: { options: mediaItemTypes },
    orientations: { options: orientation },
    productTags: {
      options: isDefined(hasProducts) ? [hasProducts.toString()] : [],
    },
    rights: { options: rightsStatuses },
    semanticTags: { tags: semanticTags },
    spam: spamFiltering,
  };
};

/**
 * Map media library sources params to sources dto for create or update collections.
 * @param sources Media library sources params
 * @returns Collection sources dto
 */
export const mapMediaLibrarySourcesToCollectionSourcesDto = ({
  accountMentionIds = [],
  curalateUserIds = [],
  pinterestUserIds = [],
  sourcesGroups = [],
  trackedBusinessUserIds = [],
  trackedHashtags = [],
  trackedUserIds = [],
}: IContentLibrarySourcesOptions): ICollectionSourcesDto => {
  return {
    allCuralateUsers: sourcesGroups.includes(
      ContentLibrarySourcesGroups.TEAM_MEDIA,
    ),
    allInstagram: sourcesGroups.includes(
      ContentLibrarySourcesGroups.ALL_INSTAGRAM,
    ),
    allInstagramBusinessUsers: sourcesGroups.includes(
      ContentLibrarySourcesGroups.ALL_TRACKED_USERS,
    ),
    allInstagramHashtags: sourcesGroups.includes(
      ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS,
    ),
    allInstagramUsers: sourcesGroups.includes(
      ContentLibrarySourcesGroups.ALL_TRACKED_USERS,
    ),
    allMedia: sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA),
    allPinterest: sourcesGroups.includes(
      ContentLibrarySourcesGroups.ALL_PINTEREST,
    ),
    allDropbox: sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_DROPBOX),
    allTikTok: sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_TIKTOK),
    allYouTube: sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_YOUTUBE),
    curalateUsers: curalateUserIds,
    instagramBusinessUsers: trackedBusinessUserIds,
    instagramHashtags: trackedHashtags,
    instagramMentions: accountMentionIds,
    instagramUsers: trackedUserIds,
    pinterestUsers: pinterestUserIds,
  };
};

/**
 * Map media library sort type to sort dto for create or update collections.
 * @param orderBy Media library sort type
 * @returns Collection sort dto if media library sort type has corresponding collection sort type
 */
export const mapMediaLibrarySortTypeToCollectionSortDto = (
  orderBy: ContentLibrarySortType,
): ICollectionSortDto | undefined => {
  const collectionSortType = deserializeOptionalEnumParam(
    CollectionSortType,
    orderBy,
  ); // Map media library sort type to collection sort type

  return isDefined(collectionSortType)
    ? {
        ascending: false, // This is not currently configurable in the frontend
        key: collectionSortType,
      }
    : undefined;
};

/**
 * Map list of strings to list of valid enum values.
 * String will be dropped if it does not match with enum definition.
 * @param enumDef Enum definition
 * @param values List of strings
 * @returns List of valid enum values
 */
export const mapStringsToValidEnumValues = <K extends string, E>(
  enumDef: { [key in K]: E },
  values: string[],
) => {
  return values.reduce<E[]>((enumValues, value) => {
    const enumValue = deserializeOptionalEnumParam(enumDef, value);
    if (isDefined(enumValue)) {
      enumValues.push(enumValue);
    }

    return enumValues;
  }, []);
};

/**
 * Map angular state name to media library sub-page. Map to `ALL_MEDIA` by default.
 * @param state Angular state name
 * @returns Media library sub-page
 */
export const mapRouterStateToSubPage = (
  state: RoutableStateName,
): MediaLibrarySubPages => {
  switch (state) {
    case 'library.team-media':
      return 'TEAM_MEDIA';
    case 'library.my-media':
      return 'MY_MEDIA';
    default:
      return 'ALL_MEDIA';
  }
};
